import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { StateService } from '@uirouter/angular';
import {
    NAVIGATION_MAP_PROVIDER,
    NavigationLinkType,
    NavigationMapProvider,
    NavigationNode,
    NavigationSection,
    SessionService
} from '@edl-development/edl-common';
import { Subscription } from 'rxjs';

@Component({
    selector: 'edl-authenticated-navigation',
    templateUrl: './edl-authenticated-navigation.component.html',
    styleUrls: ['./edl-authenticated-navigation.component.scss']
})
export class EdlAuthenticatedNavigationComponent implements OnInit, OnDestroy {
    navigationNodes: NavigationNode[] = [];
    collapsedState: Record<string, boolean> = {};

    // Menu visibility state for aria-expanded
    showMenu: boolean = false;
    private _navigationToggle?: Subscription;

    constructor(
        public stateService: StateService,
        public sessionService: SessionService,
        @Inject(NAVIGATION_MAP_PROVIDER) private navigationMapProvider: NavigationMapProvider
    ) { }

    ngOnInit() {
        this.navigationNodes = this.navigationMapProvider.getNavigationMap({
            section: NavigationSection.Sidebar
        });

        // Initialize collapsed state — expand sections with active children
        for (const node of this.navigationNodes) {
            if (node.children?.length) {
                this.collapsedState[node.id] = !this.isActive(node);
            }
        }

        // WCAG 4.1.2 - Track menu visibility state for aria-expanded
        this._navigationToggle = this.sessionService.navigationToggle.subscribe((value: boolean) => {
            this.showMenu = value;
        });
    }

    ngOnDestroy() {
        this._navigationToggle?.unsubscribe();
    }

    toggleCollapse(nodeId: string): void {
        this.collapsedState[nodeId] = !this.collapsedState[nodeId];
    }

    /**
     * Determines whether a navigation node should be highlighted as active.
     *
     * Checks in order:
     *  1. Custom isActive callback — full control over matching logic
     *  2. activeStateRoot — any state under this root triggers active
     *  3. Primary link — uses includes matching
     *  4. Children — any child's uiSref matched via includes
     */
    isActive(node: NavigationNode): boolean {
        if (node.isActive) {
            return node.isActive(this.stateService);
        }

        if (node.activeStateRoot && this.stateService.includes(node.activeStateRoot)) {
            return true;
        }

        if (node.link?.type === NavigationLinkType.State) {
            return this.stateService.includes(node.link.uiSref);
        }

        if (node.children?.length) {
            return node.children.some(child => this.isActive(child));
        }

        return false;
    }

    toggleMenu() {
        this.sessionService.toggleMenu();
    }
}
